import { Controller } from "@hotwired/stimulus"

// Three-way cover image picker for the Model (motorization) form.
// Mirrors the localization wizard feature editor: Upload / URL / Library.
// Upload → writes to model[cover_image] (file input, native multipart).
// URL    → writes to model[image] (string column, accepts direct URLs or Vimeo links).
// Library → sets hidden model[cover_media_id]; controller-side copies the
//           source MediaItem's blob into the Model's cover_image attachment
//           (or copies its URL into the image column as a fallback).
// Only one of the three fields is submitted — switching tabs clears the
// others so we don't send conflicting inputs.
export default class extends Controller {
  static targets = [
    "tabUpload", "tabUrl", "tabLibrary",
    "panelUpload", "panelUrl", "panelLibrary",
    "fileInput", "urlInput", "coverMediaIdField",
    "previewImg", "previewPlaceholder",
    "searchInput", "libraryGrid",
    "pagePrev", "pageNext", "pageCounter",
    "removeBtn", "removeFlag",
    "error"
  ]

  connect() {
    this._libraryPage = 1
    this._libraryPages = 1
    this._libraryQuery = ""
    this._librarySearchTimer = null
    this._libraryLoaded = false

    // Snapshot the page-load preview/button/flag state so cancel-on-pick
    // restores it. For a new model: empty preview + hidden button. For
    // an existing model with a cover: server-rendered img + visible
    // button.
    this._initialPreview = {
      previewSrc: this.hasPreviewImgTarget ? this.previewImgTarget.getAttribute("src") : null,
      previewHidden: this.hasPreviewImgTarget ? this.previewImgTarget.classList.contains("hidden") : true,
      placeholderHidden: this.hasPreviewPlaceholderTarget ? this.previewPlaceholderTarget.classList.contains("hidden") : false,
      btnHidden: this.hasRemoveBtnTarget ? this.removeBtnTarget.classList.contains("hidden") : true,
      flag: this.hasRemoveFlagTarget ? this.removeFlagTarget.value : "0"
    }
  }

  // ─── Tab switching ────────────────────────────────────────────

  showUpload(event) {
    if (event) event.preventDefault()
    this._activate(this.tabUploadTarget, this.panelUploadTarget)
    this.tabUrlTarget.classList.remove("tab-active")
    this.tabLibraryTarget.classList.remove("tab-active")
    this.panelUrlTarget.classList.add("hidden")
    this.panelLibraryTarget.classList.add("hidden")
  }

  showUrl(event) {
    if (event) event.preventDefault()
    this._activate(this.tabUrlTarget, this.panelUrlTarget)
    this.tabUploadTarget.classList.remove("tab-active")
    this.tabLibraryTarget.classList.remove("tab-active")
    this.panelUploadTarget.classList.add("hidden")
    this.panelLibraryTarget.classList.add("hidden")
  }

  showLibrary(event) {
    if (event) event.preventDefault()
    this._activate(this.tabLibraryTarget, this.panelLibraryTarget)
    this.tabUploadTarget.classList.remove("tab-active")
    this.tabUrlTarget.classList.remove("tab-active")
    this.panelUploadTarget.classList.add("hidden")
    this.panelUrlTarget.classList.add("hidden")
    if (!this._libraryLoaded) {
      this._libraryPage = 1
      this._fetchLibrary()
    }
  }

  _activate(tab, panel) {
    tab.classList.add("tab-active")
    panel.classList.remove("hidden")
  }

  // ─── File input ───────────────────────────────────────────────

  fileChanged() {
    const file = this.hasFileInputTarget && this.fileInputTarget.files[0]
    if (!file) {
      // No file (image-editor cancelled and cleared the input).
      // Restore the page-load state — for a new model that's the
      // placeholder + hidden remove button; for an existing model with
      // a cover that's the server-rendered img + visible button.
      this._restoreInitialPreview()
      return
    }
    // Pick wins over URL + library.
    this._clearUrl()
    this._clearLibrary()
    this._undoRemoveFlag()
    const reader = new FileReader()
    reader.onload = (e) => this._renderPreview(e.target.result)
    reader.readAsDataURL(file)
  }

  // ─── URL input ────────────────────────────────────────────────

  urlChanged() {
    const url = (this.urlInputTarget.value || "").trim()
    // Typing a URL wins over file + library.
    if (url) {
      this._clearFile()
      this._clearLibrary()
      this._undoRemoveFlag()
      if (/\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(url)) {
        this._renderPreview(url)
      } else {
        this._renderPlaceholder()
      }
    } else {
      this._renderPlaceholder()
    }
  }

  // ─── Library picker ───────────────────────────────────────────

  librarySearchChanged() {
    clearTimeout(this._librarySearchTimer)
    this._librarySearchTimer = setTimeout(() => {
      this._libraryQuery = (this.searchInputTarget.value || "").trim()
      this._libraryPage = 1
      this._fetchLibrary()
    }, 250)
  }

  libraryPrev(event) {
    if (event) event.preventDefault()
    if (this._libraryPage <= 1) return
    this._libraryPage -= 1
    this._fetchLibrary()
  }

  libraryNext(event) {
    if (event) event.preventDefault()
    if (this._libraryPage >= this._libraryPages) return
    this._libraryPage += 1
    this._fetchLibrary()
  }

  _fetchLibrary() {
    this.libraryGridTarget.innerHTML =
      "<div class='col-span-4 text-xs text-base-content/40 py-4 text-center'>…</div>"
    const params = new URLSearchParams()
    params.set("page", this._libraryPage)
    params.set("per_page", 24)
    if (this._libraryQuery) params.set("q", this._libraryQuery)

    fetch(`/admin/media_items.json?${params}`, {
      headers: this._jsonHeaders()
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(payload => {
        this._libraryLoaded = true
        const items = payload.items || []
        const pagy  = payload.pagy  || { page: 1, pages: 1, count: items.length }
        this._libraryPages = pagy.pages || 1
        this._renderLibrary(items)
        this._updateLibraryPagination(pagy)
      })
      .catch(() => {
        this.libraryGridTarget.innerHTML =
          "<div class='col-span-4 text-xs text-error py-4 text-center'>Falha ao carregar biblioteca.</div>"
      })
  }

  _renderLibrary(items) {
    if (!items.length) {
      this.libraryGridTarget.innerHTML =
        "<div class='col-span-4 text-xs text-base-content/40 py-6 text-center'>—</div>"
      return
    }
    this.libraryGridTarget.innerHTML = ""
    items.forEach(mi => {
      const tile = document.createElement("button")
      tile.type = "button"
      tile.className = "relative aspect-video rounded overflow-hidden bg-base-200 group cursor-pointer ring-0 hover:ring-2 hover:ring-primary transition"
      tile.setAttribute("data-action", "click->cover-image-picker#libraryPick")
      tile.setAttribute("data-media-id", mi.id)
      tile.setAttribute("data-media-type", mi.media_type || "")
      tile.setAttribute("data-media-preview", this._bestSrc(mi))
      tile.innerHTML = this._mediaTileInner(mi)
      this.libraryGridTarget.appendChild(tile)
    })
  }

  _updateLibraryPagination(pagy) {
    this.pageCounterTarget.textContent = `${pagy.page || 1} / ${pagy.pages || 1}`
    this.pagePrevTarget.disabled = (pagy.page || 1) <= 1
    this.pageNextTarget.disabled = (pagy.page || 1) >= (pagy.pages || 1)
  }

  libraryPick(event) {
    event.preventDefault()
    const tile = event.currentTarget
    const id = tile.dataset.mediaId
    if (!id) return

    // Pick wins over upload + URL.
    this._clearFile()
    this._clearUrl()
    this._undoRemoveFlag()

    this.coverMediaIdFieldTarget.value = id
    const src = tile.dataset.mediaPreview
    if (src) this._renderPreview(src); else this._renderPlaceholder()

    // Visually mark the selected tile.
    this.libraryGridTarget.querySelectorAll("button[data-media-id]").forEach(b => {
      b.classList.toggle("ring-2", b === tile)
      b.classList.toggle("ring-primary", b === tile)
    })
  }

  // ─── Remove ───────────────────────────────────────────────────

  // Clears every input the picker controls and flips the hidden flag the
  // server reads on submit to purge the Active Storage attachment +
  // clear the legacy URL column.
  removeCover(event) {
    if (event) event.preventDefault()
    this._clearFile()
    this._clearUrl()
    this._clearLibrary()
    this._renderPlaceholder()
    if (this.hasRemoveFlagTarget) this.removeFlagTarget.value = "1"
    if (this.hasRemoveBtnTarget)  this.removeBtnTarget.classList.add("hidden")
  }

  // When the user uploads/pastes/library-picks a new image after clicking
  // Remove, take the flag back down — the new pick should win.
  _undoRemoveFlag() {
    if (this.hasRemoveFlagTarget) this.removeFlagTarget.value = "0"
    if (this.hasRemoveBtnTarget)  this.removeBtnTarget.classList.remove("hidden")
  }

  // ─── Preview + clears ─────────────────────────────────────────

  _renderPreview(src) {
    if (!this.hasPreviewImgTarget) return
    this.previewImgTarget.src = src
    this.previewImgTarget.classList.remove("hidden")
    if (this.hasPreviewPlaceholderTarget) this.previewPlaceholderTarget.classList.add("hidden")
  }

  _renderPlaceholder() {
    if (this.hasPreviewImgTarget) {
      this.previewImgTarget.removeAttribute("src")
      this.previewImgTarget.classList.add("hidden")
    }
    if (this.hasPreviewPlaceholderTarget) this.previewPlaceholderTarget.classList.remove("hidden")
  }

  // Restores the preview + remove-button state captured in connect().
  // Used when image-editor cancels and clears the file input. Both the
  // "new model" (everything empty) and "existing model with cover"
  // (preview visible + button visible) cases collapse to the page-load
  // snapshot.
  _restoreInitialPreview() {
    const s = this._initialPreview || {}
    if (this.hasPreviewImgTarget) {
      if (s.previewSrc) {
        this.previewImgTarget.src = s.previewSrc
        this.previewImgTarget.classList.toggle("hidden", Boolean(s.previewHidden))
      } else {
        this.previewImgTarget.removeAttribute("src")
        this.previewImgTarget.classList.add("hidden")
      }
    }
    if (this.hasPreviewPlaceholderTarget) {
      this.previewPlaceholderTarget.classList.toggle("hidden", Boolean(s.placeholderHidden))
    }
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.toggle("hidden", s.btnHidden !== false)
    }
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = s.flag || "0"
    }
  }

  _clearFile() {
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
  }

  _clearUrl() {
    if (this.hasUrlInputTarget) this.urlInputTarget.value = ""
  }

  _clearLibrary() {
    if (this.hasCoverMediaIdFieldTarget) this.coverMediaIdFieldTarget.value = ""
    if (this.hasLibraryGridTarget) {
      this.libraryGridTarget.querySelectorAll("button[data-media-id]").forEach(b => {
        b.classList.remove("ring-2", "ring-primary")
      })
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────

  _mediaTileInner(mi) {
    const src = this._bestSrc(mi)
    if (mi.media_type === "video") {
      if (src) {
        return `
          <img src="${this._escape(src)}" class="w-full h-full object-cover" loading="lazy" alt="" />
          <div class="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white/90" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
            </svg>
          </div>`
      }
      return `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/50">video</div>`
    }
    if (src) {
      return `<img src="${this._escape(src)}" class="w-full h-full object-cover" loading="lazy" alt="" />`
    }
    return `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/50">${this._escape(mi.media_type || "")}</div>`
  }

  _bestSrc(mi) {
    return mi.variant_url || mi.thumbnail_url || mi.url || ""
  }

  _jsonHeaders() {
    return {
      "Accept": "application/json",
      "X-CSRF-Token": this._csrfToken(),
      "X-Requested-With": "XMLHttpRequest"
    }
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
